// src/components/CreateAgentForm.jsx
import React, { useState, useEffect } from 'react';
import { agentService } from '../services/agentService';
import { useAvailableModels } from '../hooks/useAvailableModels';
import './CreateAgentForm.css';

const CreateAgentForm = ({ onAgentCreated, onCancel }) => {
  const LLM_MODELS = useAvailableModels();
  const [formData, setFormData] = useState({
    agent_name: '',
    datasource_id: '',
    selected_tables: [],
    llm_model: 'claude-haiku-4-5',
  });

  const [datasources, setDatasources] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [error, setError] = useState('');

  // Load datasources on component mount
  useEffect(() => {
    loadDatasources();
  }, []);

  const loadDatasources = async () => {
    try {
      setLoading(true);
      const response = await agentService.getDatasourcesWithMetadata();
      setDatasources(response.datasources || []);
    } catch (error) {
      setError('Failed to load datasources: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDatasourceChange = async (datasourceId) => {
    setFormData(prev => ({
      ...prev,
      datasource_id: datasourceId,
      selected_tables: [] // Reset selected tables when datasource changes
    }));

    if (datasourceId) {
      try {
        setLoadingTables(true);
        setError('');
        const response = await agentService.getTablesWithMetadata(datasourceId);
        setTables(response.tables || []);
      } catch (error) {
        setError('Failed to load tables: ' + error.message);
        setTables([]);
      } finally {
        setLoadingTables(false);
      }
    } else {
      setTables([]);
    }
  };

  const handleTableSelection = (tableName, isSelected) => {
    setFormData(prev => ({
      ...prev,
      selected_tables: isSelected
        ? [...prev.selected_tables, tableName]
        : prev.selected_tables.filter(t => t !== tableName)
    }));
  };

  const handleSelectAllTables = (selectAll) => {
    setFormData(prev => ({
      ...prev,
      selected_tables: selectAll ? tables.map(t => t.table_name) : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agent_name.trim()) {
      setError('Agent name is required');
      return;
    }
    
    if (!formData.datasource_id) {
      setError('Please select a datasource');
      return;
    }
    
    if (formData.selected_tables.length === 0) {
      setError('Please select at least one table');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await agentService.createAgent({
        agent_name: formData.agent_name.trim(),
        datasource_id: parseInt(formData.datasource_id),
        selected_tables: formData.selected_tables,
        llm_model: formData.llm_model,
      });

      console.log('Agent created successfully:', response);
      onAgentCreated(response);
      
      // Reset form
      setFormData({
        agent_name: '',
        datasource_id: '',
        selected_tables: []
      });
      setTables([]);
      
    } catch (error) {
      setError('Failed to create agent: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="create-agent-form">
      <div className="form-header">
        <h2>Create New Agent</h2>
        <button 
          type="button" 
          className="close-btn"
          onClick={onCancel}
        >
          ×
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Agent Name */}
        <div className="form-group">
          <label htmlFor="agentName">Agent Name *</label>
          <input
            id="agentName"
            type="text"
            value={formData.agent_name}
            onChange={(e) => setFormData(prev => ({ ...prev, agent_name: e.target.value }))}
            placeholder="Enter agent name (e.g., Sales Analytics Agent)"
            required
          />
        </div>

        {/* Datasource Selection */}
        <div className="form-group">
          <label htmlFor="datasource">Select Datasource *</label>
          <select
            id="datasource"
            value={formData.datasource_id}
            onChange={(e) => handleDatasourceChange(e.target.value)}
            required
          >
            <option value="">Choose a datasource...</option>
            {datasources.map(ds => (
              <option key={ds.id} value={ds.id}>
                {ds.name}{ds.has_metadata === false ? ' (no metadata — scan first)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* AI Model Selection */}
        <div className="form-group">
          <label htmlFor="llmModel">AI Model</label>
          <select
            id="llmModel"
            value={formData.llm_model}
            onChange={(e) => setFormData(prev => ({ ...prev, llm_model: e.target.value }))}
          >
            {LLM_MODELS.map(m => (
              <option key={m.value} value={m.value}>{m.provider} — {m.label}</option>
            ))}
          </select>
        </div>

        {/* Table Selection */}
        {formData.datasource_id && (
          <div className="form-group">
            <label>Select Tables *</label>
            
            {loadingTables ? (
              <div className="loading-tables">
                <span className="spinner"></span>
                Loading available tables...
              </div>
            ) : tables.length > 0 ? (
              <>
                <div className="table-selection-header">
                  <span className="table-count">
                    {tables.length} tables available with metadata
                  </span>
                  <div className="select-all-controls">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.selected_tables.length === tables.length}
                        onChange={(e) => handleSelectAllTables(e.target.checked)}
                      />
                      Select All
                    </label>
                  </div>
                </div>
                
                <div className="table-selection-list">
                  {tables.map(table => (
                    <div key={table.table_name} className="table-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.selected_tables.includes(table.table_name)}
                          onChange={(e) => handleTableSelection(table.table_name, e.target.checked)}
                        />
                        <span className="table-name">{table.table_name}</span>
                        <span className="table-metadata">
                          Created: {new Date(table.created_on).toLocaleDateString()}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                
                {formData.selected_tables.length > 0 && (
                  <div className="selected-summary">
                    <span className="selected-icon">✓</span>
                    {formData.selected_tables.length} table(s) selected: {formData.selected_tables.join(', ')}
                  </div>
                )}
              </>
            ) : (
              <div className="no-tables-message">
                <span className="warning-icon">⚠️</span>
                No tables with metadata found for this datasource.
                <p>Please generate metadata for tables first before creating an agent.</p>
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading || !formData.agent_name || !formData.datasource_id || formData.selected_tables.length === 0}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Creating Agent...
              </>
            ) : (
              'Create Agent'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAgentForm;
